import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  addMoney,
  cashRoundingDelta,
  centavos,
  cordobasToUsd,
  formatFxRate,
  formatMoney,
  fxRate,
  parseMoney,
  subMoney,
  usdToCordobas,
  ZERO,
  type Centavos,
  type CuentaBancoCordobas,
  type Settings,
} from '@abastece/shared';
import type { Cliente, FormaPagoVenta } from '@abastece/core';
import { cuentaDeLaTarjeta } from '../lib/bancos.js';
import { db } from '../db/dexie.js';
import { normalizarReferencia, otrosUsos, useUsosDeReferencias, yaPago, type UsoDeReferencia } from '../lib/referencias.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { Campo } from './Campo.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Modal } from './Modal.js';
import { PedirPin } from './PedirPin.js';
import { useT, type Clave } from '../i18n/index.js';
import { enRango } from '../lib/costos.js';

export interface PagoBorrador {
  forma: FormaPagoVenta;
  monto: Centavos;
  montoOriginal?: Centavos;
  tasaCambio?: number;
  referencia?: string;
  estado: 'confirmado' | 'pendiente';
  /** Fiado past the client's limit: the owner who allowed it. */
  autorizadoPor?: string;
  /** A card payment's: the bank account the terminal pays into (`cuentaTarjeta`). */
  cuenta?: CuentaBancoCordobas;
}

/*
 * The one row of choices that does carry icons, against the rule in `Iconos.tsx`.
 *
 * A tab picks a view and the glyphs would all be the same kind of nothing; a tender is a
 * physical thing on the counter — notes, a phone, a card, the fiado notebook — and the six
 * pictures are six different objects. At a till, in a hurry, the shape is found before the
 * word is read.
 */
const METODOS: { forma: FormaPagoVenta; etiqueta: Clave; icono: NombreIcono }[] = [
  { forma: 'efectivo_cordobas', etiqueta: 'pago.efectivo_cordobas', icono: 'caja' },
  { forma: 'efectivo_dolares', etiqueta: 'pago.efectivo_dolares', icono: 'dolares' },
  { forma: 'transferencia', etiqueta: 'pago.transferencia', icono: 'transferencias' },
  { forma: 'billetera', etiqueta: 'pago.billetera', icono: 'billetera' },
  { forma: 'tarjeta', etiqueta: 'pago.tarjeta', icono: 'tarjeta' },
  { forma: 'credito', etiqueta: 'pago.fiado', icono: 'fiado' },
];

const esEfectivo = (f: FormaPagoVenta) => f === 'efectivo_cordobas' || f === 'efectivo_dolares';

const sumar = (pagos: PagoBorrador[]) => pagos.reduce<number>((a, p) => a + p.monto, 0) as Centavos;

/**
 * Taking the money for a sale.
 *
 * What is in the amount field counts as paid without being added first. Nearly every sale is
 * one tender for the whole bill, and the field starts out holding exactly that, so the sale is
 * one press of «Confirmar venta»; typing the note they handed over shows the change while it
 * is being typed. It used to have to go through «Agregar pago» before the sale could be
 * confirmed — a second press on every sale of the day, behind a button whose name did not tell
 * anybody that a second payment was possible at all. Paying part one way and part another now
 * has its own button, and the button says so.
 *
 * What the customer pays for having it brought (`envio`) is typed above the till's total, on any
 * sale, and comes in the total here, with «Productos + envío» under it. It was asked here, for an
 * order only (the owner, 2026-10-05), until the owner wanted it out from behind «Cobrar» (2026-10-12).
 */
export function PanelCobro({
  totalExacto: totalProductos,
  envio = ZERO,
  config,
  cliente,
  formaInicial = 'efectivo_cordobas',
  onElegirCliente,
  onCobrar,
  onCerrar,
  onRedondeo,
  textoOcupado,
}: {
  /** What the lines come to. */
  totalExacto: Centavos;
  /** What the customer pays for the delivery, typed above the till's total: on top of the lines. */
  envio?: Centavos;
  config: Settings;
  /** Back from choosing who the fiado is for: fiado, not cash, is what they were doing. */
  formaInicial?: FormaPagoVenta;
  /** Who the sale is for. Fiado needs somebody to owe it. */
  cliente: Pick<Cliente, 'id' | 'nombre' | 'limiteCredito' | 'saldo'> | null;
  /** Fiado was chosen with nobody on the sale: go and pick them. */
  onElegirCliente: () => void;
  onCobrar: (datos: {
    pagos: PagoBorrador[];
    redondeo: Centavos;
    total: Centavos;
    cambio: Centavos;
    /** What the customer paid for the delivery, already in `total`. */
    envio: Centavos;
  }) => void;
  onCerrar: () => void;
  /**
   * The cash rounding as it stands, every time it changes, and none once the panel closes: the
   * customer's screen asks for the same figure the cashier does. It showed the bill unrounded, C$ 47.30
   * on the screen while C$ 47.50 was asked for at the counter (2026-10-13).
   */
  onRedondeo?: (redondeo: Centavos) => void;
  /** What the button says once pressed, while the sale is recorded: «Confirmando con el servidor…» while the till waits for it. */
  textoOcupado?: string;
}) {
  const t = useT();
  const tasa = fxRate(config.fxTasaTienda);
  const sesion = useSesion();
  const puedeDarCredito = usePuede('dar_credito');

  /** What the bill comes to before any rounding: the lines, and the trip when there is one. */
  const totalExacto = addMoney(totalProductos, envio);

  /**
   * Cash rounding exists because centavo coins do not circulate. If no coins are involved —
   * everything settled by transfer, wallet or card — there is nothing to round, and the
   * customer pays the exact figure.
   *
   * The tender still in the field is one of the ones that count. Choosing «Tarjeta» shows the
   * exact figure straight away; judged on recorded payments alone, it suggested the rounded
   * cash figure for the card and then handed the difference back as change.
   */
  const redondeoCon = (formas: FormaPagoVenta[]): Centavos =>
    formas.every(esEfectivo) ? cashRoundingDelta(totalExacto, config.cajaRedondeoCentavos) : ZERO;

  /** What is still owed once `registrados` are in, if the rest is paid with `f`. */
  const faltaCon = (f: FormaPagoVenta, registrados: PagoBorrador[]): Centavos =>
    subMoney(
      addMoney(totalExacto, redondeoCon([...registrados.map((p) => p.forma), f])),
      sumar(registrados),
    );

  /**
   * Dollars that cover `cordobas`: a cent more when the nearest falls short. C$ 100 is US$ 2.7304,
   * and the US$ 2.73 it was suggested as came to C$ 99.98, two centavos still owed (2026-10-13).
   */
  const dolaresQueCubren = (cordobas: Centavos): Centavos => {
    const usd = cordobasToUsd(cordobas, tasa);
    return usdToCordobas(usd, tasa) < cordobas ? centavos(usd + 1) : usd;
  };

  /** What is still owed, written the way tender `f` wants it typed. */
  const textoMonto = (falta: Centavos, f: FormaPagoVenta): string => {
    const v = falta > 0 ? falta : ZERO;
    return String((f === 'efectivo_dolares' ? dolaresQueCubren(v) : v) / 100);
  };

  const [pagos, setPagos] = useState<PagoBorrador[]>([]);
  const [forma, setForma] = useState<FormaPagoVenta>(formaInicial);
  const [monto, setMonto] = useState(() => textoMonto(faltaCon(formaInicial, []), formaInicial));
  const [referencia, setReferencia] = useState('');
  /** Pressed «Dividir entre formas de pago»: the field now takes one part at a time. */
  const [partiendo, setPartiendo] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /** Fiado past the limit, and a cashier at the till: the owner's PIN is on screen. */
  const [pidiendoDueno, setPidiendoDueno] = useState(false);
  const cajaMonto = useRef<HTMLDivElement>(null);
  const botonConfirmar = useRef<HTMLButtonElement>(null);

  const pagado = sumar(pagos);
  const formasRegistradas = pagos.map((p) => p.forma);
  /** The recorded parts already cover it: nothing left to type, and no field to type it in. */
  const saldado =
    pagos.length > 0 && pagado >= addMoney(totalExacto, redondeoCon(formasRegistradas));

  const redondeo = redondeoCon(saldado ? formasRegistradas : [...formasRegistradas, forma]);
  const total = addMoney(totalExacto, redondeo);
  useEffect(() => {
    onRedondeo?.(redondeo);
    return () => onRedondeo?.(ZERO);
  }, [redondeo, onRedondeo]);

  // Past what the books take — dollars typed by the million — it reads as no amount, not a crash.
  const montoIngresado = parseMoney(monto);
  const enCordobas =
    forma === 'efectivo_dolares' && montoIngresado !== null
      ? enRango(() => usdToCordobas(montoIngresado, tasa))
      : montoIngresado;
  const faltaAntes = subMoney(total, pagado);
  /**
   * Only cash can come to more than what is owed: change comes out of the drawer. Fiado, a card,
   * a transfer or a wallet payment is never more than the bill — nobody gets change out of a
   * notebook, cash back for a screenshot, or change from a card the books would not give (the
   * card was left out, and showed change the sale was then refused for, 2026-10-13).
   */
  const soloLoQueFalta = !esEfectivo(forma);
  const enCurso =
    !saldado && enCordobas !== null && enCordobas > 0
      ? soloLoQueFalta
        ? centavos(Math.max(0, Math.min(enCordobas, faltaAntes)))
        : enCordobas
      : ZERO;

  const restante = subMoney(faltaAntes, enCurso);
  const cambio = restante < 0 ? centavos(-restante) : ZERO;
  const cubierto = restante <= 0;

  /**
   * Fiado, and whether it needs the owner.
   *
   * No limit at all counts as a limit of nothing: somebody the owner never set up for fiado can
   * still be given it, on the owner's say-so. The owner at the till says so by selling; a cashier
   * needs the owner's PIN, and the owner's name rides on the payment.
   */
  const fiado = addMoney(
    sumar(pagos.filter((p) => p.forma === 'credito')),
    forma === 'credito' ? enCurso : ZERO,
  );
  const conFiado = fiado > 0 || (forma === 'credito' && !saldado);
  const limite = cliente?.limiteCredito ?? null;
  /**
   * An abono a cashier took by transfer or wallet is off what they owe already, but the money is
   * not seen yet (`fiado.ts`): until it is, the limit counts it as still owed, or a screenshot made
   * up for it would free the limit for more goods.
   */
  const porConfirmar = useLiveQuery(
    async () =>
      cliente
        ? (await db.abonos.where('clienteId').equals(cliente.id).toArray())
            .filter((a) => a.estadoPago === 'pendiente' && !a.anulado)
            .reduce((s, a) => s + a.monto, 0)
        : 0,
    [cliente?.id],
    0,
  );
  const quedaria = addMoney(cliente?.saldo ?? ZERO, centavos(porConfirmar), fiado);
  const excede = fiado > 0 && (limite === null || quedaria > limite);
  const faltaCliente = conFiado && !cliente;
  const necesitaDueno = excede && !puedeDarCredito;

  /**
   * A transfer or a wallet payment names its receipt, and a receipt pays once: the reference is
   * what the queue checks against the bank, and what gives away a screenshot shown twice.
   */
  const usos = useUsosDeReferencias();
  const pideReferencia = forma === 'transferencia' || forma === 'billetera';
  const faltaReferencia = pideReferencia && !saldado && enCurso > 0 && !referencia.trim();
  const repetida: UsoDeReferencia[] = pideReferencia
    ? [
        ...otrosUsos(usos, referencia),
        ...pagos
          .filter((p) => p.referencia && referencia.trim() && normalizarReferencia(p.referencia) === normalizarReferencia(referencia))
          .map((p, i) => ({ clave: `parte:${i}`, ticket: null, monto: p.monto, fecha: '' })),
      ]
    : [];

  function pagoEnCurso(): PagoBorrador {
    return {
      forma,
      monto: enCurso,
      // A transfer or a wallet payment waits until somebody sees the money. Fiado is not money
      // anybody has to go and check.
      estado: forma === 'transferencia' || forma === 'billetera' ? 'pendiente' : 'confirmado',
      ...(forma === 'efectivo_dolares' && montoIngresado !== null
        ? { montoOriginal: montoIngresado, tasaCambio: config.fxTasaTienda }
        : {}),
      ...((forma === 'transferencia' || forma === 'billetera') && referencia.trim()
        ? { referencia: referencia.trim() }
        : {}),
      ...(forma === 'tarjeta' ? { cuenta: cuentaDeLaTarjeta(config) } : {}),
    };
  }

  function elegirForma(f: FormaPagoVenta) {
    setForma(f);
    // Choosing a tender does not change what is owed, so re-suggest rather than clear. Clearing
    // it disabled the confirm button the moment anybody touched the row of methods sitting
    // directly above the field.
    setMonto(textoMonto(faltaCon(f, pagos), f));
  }

  /** Records the part in the field and leaves the rest of the bill there for the next tender. */
  function agregar() {
    if (enCurso <= 0 || faltaReferencia) return;
    setPagos((p) => [...p, pagoEnCurso()]);
    setMonto(textoMonto(restante, forma));
    setReferencia('');
  }

  function dividir() {
    setPartiendo(true);
    // The first part is already typed in: splitting is recording it.
    if (enCurso > 0 && !cubierto) return agregar();
    // Otherwise the field still holds the whole bill. Selected rather than emptied: the part
    // typed next replaces it, and until then the sale can still be confirmed as it stands.
    const campo = cajaMonto.current?.querySelector('input');
    campo?.focus();
    campo?.select();
  }

  function quitar(i: number) {
    const resto = pagos.filter((_, j) => j !== i);
    setPagos(resto);
    // What is owed just went back up; the field follows it.
    setMonto(textoMonto(faltaCon(forma, resto), forma));
  }

  function confirmar(dueno?: string) {
    if (!cubierto || ocupado || faltaCliente || faltaReferencia) return;
    if (necesitaDueno && !dueno) {
      setPidiendoDueno(true);
      return;
    }
    setOcupado(true);
    const todos = enCurso > 0 ? [...pagos, pagoEnCurso()] : pagos;
    // Whoever let it past the limit: the owner who typed their PIN, or the owner selling.
    const autorizadoPor = excede ? (dueno ?? sesion?.usuarioId) : undefined;
    onCobrar({
      pagos: todos.map((p) =>
        p.forma === 'credito' && autorizadoPor ? { ...p, autorizadoPor } : p,
      ),
      redondeo,
      total,
      cambio,
      envio,
    });
  }

  function ayudaMonto(): string | undefined {
    if (forma === 'efectivo_dolares' && enCordobas !== null) {
      return t('cobro.equivale', { monto: formatMoney(enCordobas), tasa: formatFxRate(tasa) });
    }
    if ((forma === 'transferencia' || forma === 'billetera') && enCordobas !== null && enCordobas > faltaAntes) {
      return t('cobro.sinVuelto', { monto: formatMoney(faltaAntes) });
    }
    if (partiendo) return pagos.length === 0 ? t('cobro.ayudaParte') : undefined;
    return forma === 'efectivo_cordobas' ? t('cobro.ayudaVuelto') : undefined;
  }

  return (
    <Modal
      titulo={t('cobro.titulo')}
      // Not while it is being charged: closed while the server confirmed it, the cart was still there,
      // and «Cobrar» again charged it twice (2026-10-13). Every way the charge ends closes it.
      onCerrar={ocupado ? () => {} : onCerrar}
      pie={
        <div className="flex flex-col gap-3">
          {cambio > 0 && (
            <div
              className="brinca flex items-baseline justify-between rounded-xl bg-amber-500/10 px-4 py-3"
              data-guia="cobro-vuelto"
            >
              <span className="text-sm font-medium text-amber-200">{t('cobro.vuelto')}</span>
              <span key={cambio} className="tabular late text-xl font-bold text-amber-200">
                {formatMoney(cambio)}
              </span>
            </div>
          )}
          <button
            ref={botonConfirmar}
            type="button"
            className="btn-primario h-16 w-full text-lg"
            data-guia="cobro-confirmar"
            disabled={!cubierto || ocupado || faltaCliente || faltaReferencia}
            onClick={() => confirmar()}
          >
            <Ico icono={necesitaDueno && cubierto ? 'bloquear' : 'confirmar'} className="size-6" />
            {ocupado
              ? (textoOcupado ?? t('cobro.registrando'))
              : !cubierto
                ? t('cobro.faltan', { monto: formatMoney(restante) })
                : faltaReferencia
                  ? t('cobro.faltaReferencia')
                  : necesitaDueno
                  ? t('fiado.pinYConfirmar')
                  : t('cobro.confirmarVenta')}
          </button>
        </div>
      }
    >
      <div className="mb-5 flex flex-col gap-1 rounded-xl bg-slate-950 px-4 py-3">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-slate-400">{t('comun.total')}</span>
          <span className="tabular text-2xl font-bold">{formatMoney(total)}</span>
        </div>
        {envio > 0 && (
          <div className="flex items-baseline justify-between text-xs text-slate-500" data-desglose-envio>
            <span>{t('cobro.productosYEnvio')}</span>
            <span className="tabular">
              {formatMoney(totalProductos, { symbol: false })} + {formatMoney(envio, { symbol: false })}
            </span>
          </div>
        )}
        {redondeo !== 0 && (
          <div className="flex items-baseline justify-between text-xs text-slate-500">
            <span>{t('cobro.redondeo')}</span>
            <span className="tabular">
              {formatMoney(totalExacto, { symbol: false })} {redondeo > 0 ? '+' : '−'}{' '}
              {formatMoney(centavos(Math.abs(redondeo)), { symbol: false })}
            </span>
          </div>
        )}
      </div>

      {pagos.length > 0 && (
        <ul className="mb-5 flex flex-col gap-2">
          {pagos.map((p, i) => (
            <li key={i} className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2">
              <div className="min-w-0">
                <span className="text-sm">
                  {(() => {
                    const clave = METODOS.find((m) => m.forma === p.forma)?.etiqueta;
                    return clave ? t(clave) : p.forma;
                  })()}
                </span>
                {p.montoOriginal && (
                  <span className="tabular ml-2 text-xs text-slate-400">
                    {t('cobro.dolaresA', {
                      monto: formatMoney(p.montoOriginal, { symbol: false }),
                      tasa: formatFxRate(fxRate(p.tasaCambio ?? config.fxTasaTienda)),
                    })}
                  </span>
                )}
                {p.estado === 'pendiente' && (
                  <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-300">
                    {t('cobro.sinConfirmar')}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="tabular text-sm font-medium">{formatMoney(p.monto)}</span>
                <button type="button" className="btn-texto gap-1 text-xs text-slate-500" onClick={() => quitar(i)}>
                  <Ico icono="quitar" className="size-3.5" />
                  {t('comun.quitar')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!saldado && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-2" data-guia="cobro-metodos">
            {METODOS.map((m) => (
              <button
                key={m.forma}
                type="button"
                onClick={() => elegirForma(m.forma)}
                className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium transition ${
                  forma === m.forma ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                <Ico icono={m.icono} className="size-5" />
                <span className="text-center leading-tight">{t(m.etiqueta)}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <div ref={cajaMonto} className="flex flex-col gap-3" data-guia="cobro-monto">
              {/*
                Not focused on opening. The field is already right for most sales, and a phone
                keyboard coming up would cover the one button they need; a scanner fired into it
                would type a barcode as the amount.
              */}
              <Campo
                etiqueta={t(forma === 'efectivo_dolares' ? 'cobro.dolaresRecibidos' : 'cobro.monto')}
                inputMode="decimal"
                enterKeyHint="done"
                className="tabular text-lg"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                // Selected on the way in, so the note handed over is typed over the suggestion
                // rather than onto the end of it.
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  // Enter never sells by itself: it puts the keyboard away and the change and the
                  // confirm button in view, or, while splitting, records the part. Cancelled
                  // first, or the same keystroke goes on to press the button it just focused.
                  e.preventDefault();
                  if (cubierto) botonConfirmar.current?.focus();
                  else if (partiendo) agregar();
                  else e.currentTarget.blur();
                }}
                ayuda={ayudaMonto()}
              />

              {(forma === 'transferencia' || forma === 'billetera') && (
                <div>
                  <Campo
                    maxLength={80}
                    etiqueta={t('cobro.referenciaObligatoria')}
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    autoComplete="off"
                    ayuda={t('cobro.ayudaReferencia')}
                  />
                  {repetida.length > 0 && (
                    <p className="mt-1.5 text-sm text-rose-300">
                      {repetida[0]!.clave.startsWith('parte:')
                        ? t('cobro.referenciaEnOtraParte')
                        : t(yaPago(repetida[0]!).clave, yaPago(repetida[0]!).valores)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {forma === 'credito' && (
              <EstadoFiado
                cliente={cliente}
                limite={limite}
                quedaria={quedaria}
                excede={excede}
                puedeDarCredito={puedeDarCredito}
                onElegirCliente={onElegirCliente}
              />
            )}

            <button
              type="button"
              className="btn-secundario"
              data-guia="cobro-dividir"
              disabled={partiendo && (enCurso <= 0 || faltaReferencia)}
              onClick={partiendo ? agregar : dividir}
            >
              <Ico icono={partiendo ? 'agregar' : 'dividir'} />
              {t(partiendo ? 'cobro.agregarPago' : 'cobro.dividir')}
            </button>
          </div>
        </>
      )}

      {pidiendoDueno && (
        <PedirPin
          motivo={t('fiado.motivoPin', { monto: formatMoney(fiado), nombre: cliente?.nombre ?? '' })}
          onCancelar={() => setPidiendoDueno(false)}
          onAutorizado={(usuarioId) => {
            setPidiendoDueno(false);
            confirmar(usuarioId);
          }}
        />
      )}
    </Modal>
  );
}

/**
 * What fiado would do to this client, said before it is done: what they owe now, their limit,
 * and what they would owe after this sale. Past the limit it says who has to agree.
 */
function EstadoFiado({
  cliente,
  limite,
  quedaria,
  excede,
  puedeDarCredito,
  onElegirCliente,
}: {
  cliente: Pick<Cliente, 'nombre' | 'saldo'> | null;
  limite: Centavos | null;
  quedaria: Centavos;
  excede: boolean;
  puedeDarCredito: boolean;
  onElegirCliente: () => void;
}) {
  const t = useT();
  if (!cliente) {
    return (
      <div className="flex flex-col gap-3 rounded-xl bg-amber-500/10 px-4 py-3">
        <p className="text-sm text-amber-200">{t('fiado.sinCliente')}</p>
        <button type="button" className="btn-secundario h-11 min-h-0 text-sm" onClick={onElegirCliente}>
          <Ico icono="clientes" className="size-4" />
          {t('fiado.escogerCliente')}
        </button>
      </div>
    );
  }
  const saldo = cliente.saldo ?? ZERO;
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-xl px-4 py-3 ${excede ? 'bg-amber-500/10' : 'bg-slate-950'}`}
      data-guia="cobro-fiado"
    >
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate font-medium">{cliente.nombre}</span>
        <span className="tabular shrink-0 text-slate-400">
          {saldo < 0
            ? t('fiado.aFavor', { monto: formatMoney(centavos(-saldo)) })
            : t('fiado.debe', { monto: formatMoney(saldo) })}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-xs text-slate-400">
        <span>{t('fiado.quedaria')}</span>
        <span className="tabular font-semibold text-slate-100">{formatMoney(quedaria)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-xs text-slate-500">
        <span>{t('fiado.limite')}</span>
        <span className="tabular">{limite === null ? t('fiado.sinFiado') : formatMoney(limite)}</span>
      </div>
      {excede && (
        <p className="mt-1 text-xs text-amber-200">
          {limite === null
            ? t('fiado.noTieneFiado', { nombre: cliente.nombre })
            : t('fiado.pasaLimite', { monto: formatMoney(subMoney(quedaria, limite)) })}{' '}
          {t(puedeDarCredito ? 'fiado.ustedAutoriza' : 'fiado.pideDueno')}
        </p>
      )}
    </div>
  );
}
